import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, TextField } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { api, apiDoctors, doctorsBaseRoute, IDoctor } from '../../types';
import { createApiHeaders } from '../../utilities/JwtTokenParser';
import Header from '../../components/Header';
import Loader from '../../components/Loader';
import { useAlert } from '../../layout/MainAlert';

const doctorPrototype : IDoctor = {
  id: 0, 
  firstName: "",
  lastName: "",
  licenceNumber: "",
  licenceReference: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  province: "",
  postal: "",
  email: "",
}

const fetchDoctorData = async (doctorId: string): Promise<IDoctor> => {
  const data = await fetch(`${api}${apiDoctors}${doctorId}`, {
    method: 'GET', 
    headers: createApiHeaders(true, true)
  });
  
  const json = await data.json();
  return json;
}

const saveDoctorData = async (doctor: IDoctor): Promise<IDoctor> => {
  const response = await fetch(`${api}${apiDoctors}${doctor.id}`, {
    method: 'PUT',
    headers: createApiHeaders(true, true),
    body: JSON.stringify(doctor)
  });

  if (response.ok) {
    const json = await response.json();
    return json;
  }

  throw new Error(`Detailed Error Code: ${response.status}.`);
}

const validationSchema = yup.object().shape({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  licenceNumber: yup.string()
    .required("Licence Number is required")
    .max(10, "Licence Number must be with a maximum length of 10"),
  licenceReference: yup.string()
    .required("Licence Reference is required")
    .test("len", "Licence Reference must be with an exact length of 2", value => value.length === 2)
});

const DoctorEdit = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const params = useParams();
  const alert = useAlert();
  
  const doctorId = params.id ?? '0';
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [doctor, setDoctor] = useState<IDoctor>(doctorPrototype);
  
  useEffect(() => {
    setIsLoading(true);
    fetchDoctorData(doctorId)
      .then((response) => { 
        setDoctor(response as IDoctor);
        setIsLoading(false);
        setIsSuccess(true); 
      })
      .catch((reason) => {
        console.error(reason);
        setIsLoading(false);
        setIsSuccess(false);
      }); 
  }, []);

  const formik = useFormik({
    initialValues: doctor,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      saveDoctorData(values)
      .then((response) => {
        alert.success(`Doctor "${response.lastName}, ${response.firstName}" saved successfully.`);
        navigate(`${doctorsBaseRoute}`);
      })
      .catch((error) => {
        alert.error(`Doctor can't be saved. ${error}`);
      });
    }
  });
  
  return (
    <Box m="14px">
      <Box 
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gap="20px" 
        sx={{
          "& > div": { gridColumn: isNonMobile ? undefined : "span 12" }
        }}
      >
        <Box 
          display="flex"
          sx={{
            gridColumn: 'span 4'
          }}
        >
          <Header title="DOCTOR INFO" subtitle="" />
        </Box>

        <Box
          display="flex" 
          justifyContent="center"
          sx={{
            gridColumn: 'span 4'
          }}
        >
          <Loader 
            isLoading={isLoading} 
            isSuccess={isSuccess} 
            loadingText="LOADING DOCTOR INFO..."
            successText="GREAT! DOCTOR INFO LOADED."
          />
        </Box>
      </Box>

      <form onSubmit={formik.handleSubmit}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(6, minmax(0, 1fr))"
          gap="20px"
          sx={{
            "& > div": { gridColumn: isNonMobile ? undefined : "span 6" }
          }}
        >
          <TextField
            fullWidth
            variant="filled"
            id="firstName"
            name="firstName"
            label="First Name"
            color="info"
            value={formik.values.firstName}
            onChange={formik.handleChange} 
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
            sx={{ gridColumn: "span 3" }}
          />
              
          <TextField
            fullWidth
            variant="filled"
            id="lastName"
            name="lastName"
            label="Last Name"
            color="info"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
            sx={{ gridColumn: "span 3" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="licenceNumber"
            name="licenceNumber"
            label="Licence Number"
            color="info"
            value={formik.values.licenceNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.licenceNumber && Boolean(formik.errors.licenceNumber)}
            helperText={formik.touched.licenceNumber && formik.errors.licenceNumber}
            sx={{ gridColumn: "span 3" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="licenceReference"
            name="licenceReference"
            label="Licence Reference"
            color="info"
            value={formik.values.licenceReference}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.licenceReference && Boolean(formik.errors.licenceReference)}
            helperText={formik.touched.licenceReference && formik.errors.licenceReference}
            sx={{ gridColumn: "span 3" }}
          />
        </Box>
            
        <Box display="flex" justifyContent="end" mt="20px">
          <Button 
            type="submit" 
            variant="contained" 
            color="secondary"
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              padding: "8px 14px",
            }}
          >
            Save Changes
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default DoctorEdit;